import { AccountDataFieldsValidation } from '../../../shared/helpers/UserValidation';
import Immutable from 'immutable';
import { useController, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import analytics from '../../../shared/services/analytics/analytics';
import { useSelector } from 'react-redux';
import merge from 'lodash/merge';
import * as wizardActions from '../../../actions/AccountActions/AccountWizardActions';
import { changeFormField } from '../../../actions/AccountActions/AccountWizardActions';
import { IntlShape, useIntl } from 'react-intl';
import { accountOptionsSelector, dataFieldsSelector } from '../../../reducers/AccountReducer/AccountWizardSelector';
import { composeValidations } from './validations';
import { WizardControllerProps } from '../FormFields/types';
import { useAppDispatch } from '../../../shared/services/store/useAppDispatch';

export const userValidation = new AccountDataFieldsValidation();

export const getFormFieldConfig = ({
  name,
  accountOptions,
  replaceValidations,
  rules = {},
  override = {},
  intl,
  ...rest
}: {
  name: string;
  accountOptions?: any;
  replaceValidations?: string[];
  rules?: any;
  override?: any;
  intl: IntlShape;
}) => {
  //   todo: fix types
  const validationKeys = replaceValidations ?? (userValidation.getRules() as any)[name];

  const validate = composeValidations(
    ...(validationKeys
      .flatMap((key: string) => (userValidation as any)[key]?.bind(userValidation))
      .map((validation: Function) => (value: any, formValues: any) => {
        const immutableFormValues = convertValuesToImmutableJS(formValues);
        return validation?.(value, immutableFormValues, accountOptions, intl);
      }) ?? [])
  );
  return merge(
    {
      ...rest,
      name,
      rules: { ...rules, validate, deps: rules?.deps?.concat((userValidation.fieldsWithDependencies as any)[name] ?? []) },
    },
    override
  );
};

export const convertValuesToImmutableJS = (formValues: any) => {
  const newObject = {} as any;

  for (let key in formValues) {
    newObject[key] = { value: formValues[key] };
  }

  return Immutable.fromJS(newObject);
};

export const prepareStructureForSubmit = (formValues: any) => {
  let newMap = Immutable.Map({});

  for (let key of formValues.keys()) {
    newMap = newMap.set(key, Immutable.fromJS({ value: formValues.get(key), valid: true }));
  }

  return newMap;
};

export const useWizardFormController = (controllerProps: WizardControllerProps) => {
  const accountOptions = useSelector(accountOptionsSelector);
  const intl = useIntl();
  const fieldConfig = getFormFieldConfig({
    ...controllerProps,
    intl,
    accountOptions,
  });

  const dispatch = useAppDispatch();

  const controller = useController(fieldConfig);

  const onChange = useCallback(
    (...args: any[]) => {
      controller.field.onChange(...args);
      dispatch(changeFormField({ name: controller.field.name, value: args[0].target.value }));
    },
    [controller.field.onChange]
  );

  const onBlur = useCallback(
    (...args: any[]) => {
      controller.field.onBlur();
      if (controller.fieldState.invalid) {
        return analytics.trackCustomEvent('wizard_sign_up_field_error', { event_label: controllerProps.name });
      }

      return analytics.trackCustomEventOnce('wizard_sign_up_field_complete', { event_label: controllerProps.name });
    },
    [controller.field.onBlur]
  );

  const wizardController = useMemo(
    () => ({
      ...controller,
      // onChangeOriginal is used when we change field value in effects or we don't want to use analytics
      field: {
        ...controller.field,
        onChange,
        onChangeOriginal: controller.field.onChange,
        onBlur,
        onBlurOriginal: controller.field.onBlur,
      },
    }),
    [controller]
  );

  return wizardController;
};

export const useAccountCreateForm = () => {
  const dataFields = useSelector(dataFieldsSelector);

  const form = useForm<any>({
    criteriaMode: 'all',
    mode: 'onTouched',
    defaultValues: dataFields.toJS(),
  });

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(wizardActions.setStageForm(form));
  }, [form]);

  return form;
};
export const queryWizardField = (name: string) =>
  document.querySelector(`[for="${name}"]`) || document.querySelector(`[name="${name}"]`) || document.getElementById(name);
